/**
 * The pieces of GOOD BUILD that are the brand rather than the interface: the stripe, the
 * lockup, the build number, the status tags, progress, and the shelf.
 *
 * Chapter 05 of the guide calls these the graphic language. They are a small vocabulary on
 * purpose — a screen that uses all of them at once is a screen that is shouting.
 */

import type * as React from 'react';
import { GAME_KIND_LABELS, GAME_KIND_WARNINGS, type GameKind } from '@goodbuild/shared';
import { Eyebrow, Text } from './primitives.js';
import { Icon } from './icons.js';

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/* ------------------------------------------------------------------ stripe */

/**
 * The mark. Two lemon bars at 45 degrees, sized from the font size of whatever contains it,
 * so it scales with the lockup instead of needing its own size prop.
 */
export function Stripe({ className }: { className?: string }) {
  return <i className={cx('gb-stripe', className)} aria-hidden="true" />;
}

/**
 * Stripe, wordmark, and the product name beside it in lemon — the top-left of the board.
 *
 * `product` is dropped on a phone, where the same bar has to hold a search control too.
 */
export function Lockup({ product, className }: { product?: string; className?: string }) {
  return (
    <span className={cx('gb-lockup', className)}>
      <Stripe />
      <span>{'GOOD BUILD'}</span>
      {product ? <span className="gb-lockup__os">{product}</span> : null}
    </span>
  );
}

/* ------------------------------------------------------------------- build */

/**
 * A build number, said plainly. `BUILD 0.1.7`.
 *
 * Voice rule: use real build numbers. The studio being specific about where the work is, is
 * the whole point of the label.
 */
export function BuildTag({
  version,
  prefix = 'BUILD',
  className,
}: {
  version: string;
  prefix?: string;
  className?: string;
}) {
  return (
    <span className={cx('gb-build', className)}>
      <span className="gb-build__hash">{prefix}</span>
      <span>{version}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ status */

export type StatusKind = 'development' | 'live' | 'listed' | 'locked' | 'unavailable';

const STATUS: Record<StatusKind, { label: string; tone: string }> = {
  development: { label: 'In development', tone: 'gb-badge--lemon' },
  live: { label: 'Live now', tone: 'gb-badge--jade' },
  listed: { label: 'On your list', tone: 'gb-badge--fog' },
  locked: { label: 'Supporters only', tone: 'gb-badge--sand' },
  unavailable: { label: 'Not on this device', tone: 'gb-badge--fog' },
};

/**
 * The three status tags from the board, plus the two this app actually needs.
 *
 * The word carries the meaning and the fill carries the tone, so the tag still works for
 * someone who cannot tell lemon from jade (field rule 04).
 */
export function StatusTag({
  kind,
  label,
  className,
}: {
  kind: StatusKind;
  label?: string;
  className?: string;
}) {
  const status = STATUS[kind];
  return <span className={cx('gb-badge', status.tone, className)}>{label ?? status.label}</span>;
}

/**
 * How finished a game is, as one tag.
 *
 * The tone walks down the ladder with the kind — jade for something finished, then lemon,
 * sand and fog — so the colour agrees with the word instead of repeating it.
 */
const KIND_TONE: Record<GameKind, string> = {
  game: 'gb-badge--jade',
  early_access: 'gb-badge--lemon',
  dev: 'gb-badge--sand',
  prototype: 'gb-badge--fog',
};

export function GameKindTag({ kind, className }: { kind: GameKind; className?: string }) {
  return (
    <span className={cx('gb-badge', KIND_TONE[kind], className)}>{GAME_KIND_LABELS[kind]}</span>
  );
}

/**
 * The same rung, said at the size it deserves.
 *
 * `GameKindTag` names the rung; this one says what the word costs you, and it is loud on
 * purpose. Someone about to spend an evening and twenty gigabytes on a prototype should have
 * been told, in a sentence they could not miss, that it may not start and may never be
 * finished. Copy lives in `GAME_KIND_WARNINGS` so the client and the site cannot drift.
 *
 * The tone runs the opposite way to the tag's, and that is deliberate: the tag colours by how
 * finished a thing is (jade at the top, fog at the bottom), this colours by how much risk is
 * being taken on, which is highest at the bottom of the same ladder. A finished game renders
 * nothing — a warning on every screen is a warning nobody reads.
 */
const KIND_WARNING_TONE: Record<GameKind, string> = {
  game: '',
  early_access: 'gb-warn--sand',
  dev: 'gb-warn--lemon',
  prototype: 'gb-warn--danger',
};

export function GameKindBanner({
  kind,
  /** Half height, for a shelf heading rather than the one screen someone decides on. */
  compact = false,
  className,
}: {
  kind: GameKind;
  compact?: boolean;
  className?: string;
}) {
  const warning = GAME_KIND_WARNINGS[kind];
  if (!warning) return null;

  return (
    <aside
      role="note"
      className={cx('gb-warn', KIND_WARNING_TONE[kind], compact && 'gb-warn--compact', className)}
    >
      <Icon name="warning" size={compact ? 22 : 30} className="gb-warn__icon" />
      <div className="gb-warn__copy">
        {/* The rung is named again here rather than assumed: this banner is quoted over a
            shelf as well, where the tag it belongs to is not on screen. */}
        <Eyebrow className="gb-warn__label">{GAME_KIND_LABELS[kind]}</Eyebrow>
        <p className="gb-display gb-warn__headline">{warning.headline}</p>
        <p className="gb-warn__body">{warning.body}</p>
      </div>
    </aside>
  );
}

/* ---------------------------------------------------------------- progress */

/**
 * A measurement, or an admission that there is not one yet.
 *
 * `null` is not nought. A job that has just changed phase has measured nothing of the new
 * quantity, and drawing that as an empty bar says the opposite of what is true: it reads as a
 * download that has stalled at the start line. So a bar with no figure sweeps instead, which
 * says "working" without claiming a number, and drops `aria-valuenow` — which is exactly how a
 * screen reader is told a progressbar is indeterminate.
 */
export function ProgressBar({
  value,
  label,
  className,
}: {
  /** 0 to 1, or null while there is nothing measured to draw. */
  value: number | null;
  label?: string;
  className?: string;
}) {
  const waiting = value === null || !Number.isFinite(value);
  const pct = waiting ? 0 : Math.max(0, Math.min(1, value)) * 100;
  return (
    <div
      className={cx('gb-progress', waiting && 'gb-progress--waiting', className)}
      role="progressbar"
      aria-valuenow={waiting ? undefined : Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <span className="gb-progress__bar" style={waiting ? undefined : { width: `${pct}%` }} />
    </div>
  );
}

/* ------------------------------------------------------------------ layout */

/**
 * A screen opens the way a chapter of the guide opens: a mono label, then one Sora line, then
 * at most one sentence of explanation.
 */
export function PageHead({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <header className="gb-page-head">
      <Eyebrow>{eyebrow}</Eyebrow>
      <div className="gb-row gb-row--between">
        <h1 className="gb-display gb-display--3">{title}</h1>
        {actions ? <div className="gb-row gb-row--tight">{actions}</div> : null}
      </div>
      {description ? (
        <Text render={<p />} size="3" color="gray" style={{ maxWidth: '48rem' }}>
          {description}
        </Text>
      ) : null}
    </header>
  );
}

/** The heading over a shelf: `GAMES`, with an optional quiet note on the right. */
export function ShelfHead({ title, note }: { title: string; note?: React.ReactNode }) {
  return (
    <div className="gb-shelf-head">
      <Eyebrow>{title}</Eyebrow>
      {note ? (
        <Text size="1" color="gray">
          {note}
        </Text>
      ) : null}
    </div>
  );
}

/**
 * A row that runs off the right edge on purpose, with a chevron saying there is more.
 *
 * The chevron is decoration, not a control: the row is scrolled with a finger, a wheel, the
 * arrow keys or the stick, all of which already work.
 */
export function ShelfFrame({
  children,
  more = true,
  className,
}: {
  children: React.ReactNode;
  more?: boolean;
  className?: string;
}) {
  return (
    <div className={cx('gb-shelf-wrap', className)}>
      {children}
      {more ? (
        <span className="gb-shelf-more" aria-hidden="true">
          <Icon name="chevron-right" size={22} />
        </span>
      ) : null}
    </div>
  );
}
